/**
 * Helper script to initialize holidays in Firestore
 *
 * Firestore structure:
 * /config/holidays/{yyyy}
 *
 * Document fields:
 * {
 *   year: 2025,
 *   holidays: [
 *     { date: "2025-01-01", name: "New Year's Day" },
 *     { date: "2025-04-13", name: "Sinhala & Tamil New Year" },
 *     ...
 *   ]
 * }
 */

import { db } from '../firebase';
import { doc, setDoc } from 'firebase/firestore';

/**
 * Sri Lankan Public Holidays 2025
 * Source: https://www.officeholidays.com/countries/sri-lanka
 */
const HOLIDAYS_2025 = [
  { date: "2025-01-14", name: "Thai Pongal" },
  { date: "2025-02-04", name: "Independence Day" },
  { date: "2025-03-14", name: "Maha Shivaratri" },
  { date: "2025-04-13", name: "Sinhala & Tamil New Year's Eve" },
  { date: "2025-04-14", name: "Sinhala & Tamil New Year" },
  { date: "2025-04-18", name: "Good Friday" },
  { date: "2025-05-01", name: "May Day" },
  { date: "2025-05-12", name: "Vesak Full Moon Poya Day" },
  { date: "2025-05-13", name: "Day following Vesak" },
  { date: "2025-06-10", name: "Poson Full Moon Poya Day" },
  { date: "2025-08-15", name: "Nikini Full Moon Poya Day" },
  { date: "2025-09-13", name: "Binara Full Moon Poya Day" },
  { date: "2025-10-12", name: "Vap Full Moon Poya Day" },
  { date: "2025-11-10", name: "Il Full Moon Poya Day" },
  { date: "2025-12-10", name: "Unduvap Full Moon Poya Day" },
  { date: "2025-12-25", name: "Christmas Day" },
];

/**
 * Sri Lankan Public Holidays 2026 (partial - update as needed)
 */
const HOLIDAYS_2026 = [
  { date: "2026-01-01", name: "New Year's Day" },
  { date: "2026-01-14", name: "Thai Pongal" },
  { date: "2026-02-04", name: "Independence Day" },
  { date: "2026-04-13", name: "Sinhala & Tamil New Year's Eve" },
  { date: "2026-04-14", name: "Sinhala & Tamil New Year" },
  { date: "2026-05-01", name: "May Day" },
  { date: "2026-12-25", name: "Christmas Day" },
];

/**
 * Initialize holidays for a specific year
 * @param {number} year
 * @param {Array} holidays
 */
export async function initializeHolidaysForYear(year, holidays) {
  try {
    const docRef = doc(db, 'config', `holidays_${year}`);
    await setDoc(docRef, {
      year,
      holidays,
      updatedAt: new Date(),
    });
    console.log(`✅ Holidays initialized for ${year}`);
    return true;
  } catch (error) {
    console.error(`❌ Error initializing holidays for ${year}:`, error);
    return false;
  }
}

/**
 * Initialize all holidays
 */
export async function initializeAllHolidays() {
  console.log('Initializing holidays in Firestore...');

  await initializeHolidaysForYear(2025, HOLIDAYS_2025);
  await initializeHolidaysForYear(2026, HOLIDAYS_2026);

  console.log('✅ All holidays initialized!');
}

/**
 * Fetch holidays for a specific year from Firestore
 * @param {number} year
 * @returns {Promise<Array<string>>} Array of date strings (YYYY-MM-DD)
 */
export async function fetchHolidaysForYear(year) {
  try {
    const { doc: docFunc, getDoc } = await import('firebase/firestore');
    const docRef = docFunc(db, 'config', `holidays_${year}`);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.holidays.map(h => h.date);
    }

    console.warn(`No holidays found for year ${year}`);
    return [];
  } catch (error) {
    console.error(`Error fetching holidays for ${year}:`, error);
    return [];
  }
}

/**
 * Fetch holidays for current year and next year
 * @returns {Promise<Array<string>>} Array of date strings (YYYY-MM-DD)
 */
export async function fetchCurrentAndNextYearHolidays() {
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  const [currentYearHolidays, nextYearHolidays] = await Promise.all([
    fetchHolidaysForYear(currentYear),
    fetchHolidaysForYear(nextYear),
  ]);

  return [...currentYearHolidays, ...nextYearHolidays];
}

// Export holiday data for direct use if needed
export { HOLIDAYS_2025, HOLIDAYS_2026 };
